import { FeedItemSubType, FeedItemType, Frequency } from '../models/Feed';
export default class FeedUtils {
    static topics = [
        FeedItemType.PROJECT,
        FeedItemType.SCHEDULE,
        FeedItemType.RFI,
        FeedItemType.PHOTO,
        FeedItemType.SUBMITTAL,
        FeedItemType.LOG,
        FeedItemType.CONTRACT,
    ];
    static frequency = [Frequency.HOURLY, Frequency.DAILY, Frequency.WEEKLY, Frequency.NEVER];
    static getHash(projectId, feedItem) {
        switch (feedItem.type) {
            case FeedItemType.RFI:
                return '/project/' + projectId + '/rfi/' + feedItem.related_id;
            case FeedItemType.SCHEDULE:
                if (feedItem.related_id) {
                    //      project/demo/schedule/zoom/2025-11-15/wbs/1.3.10
                    return `/project/${projectId}/schedule/default/activity/${feedItem.related_id}/panel/info`;
                }
                else {
                    return '/project/' + projectId + '/schedule';
                }
            case FeedItemType.SUBMITTAL:
                return `/project/${projectId}/submittals/${feedItem.related_id}`;
            case FeedItemType.PHOTO:
                return '/project/' + projectId + '/photos';
            case FeedItemType.SCRAPBOOK:
                return '/project/' + projectId + '/documents';
            case FeedItemType.PROJECT:
                switch (feedItem.sub) {
                    case FeedItemSubType.INVITE:
                        return '/project/' + projectId + '/directory';
                    case FeedItemSubType.PHOTO:
                        return '/project/' + projectId + '/photos';
                    case FeedItemSubType.DRAWING:
                        return '/project/' + projectId + '/documents/drawings';
                }
            default:
                return '/project/' + projectId + '/dashboard';
        }
    }
    static getItemDesc(people, context, item, dbPrefix) {
        switch (item.type) {
            case FeedItemType.PROJECT:
                return this.getProjectText(item, people);
            case FeedItemType.SCHEDULE:
                return this.getScheduleItemDesc(item, people, context);
            case FeedItemType.RFI:
                return this.getRfiItemDesc(item, people, context);
            case FeedItemType.CONTRACT:
                return FeedUtils.contractItemDescription(item, dbPrefix, context);
            case FeedItemType.SUBMITTAL:
                return FeedUtils.submittalItemDesc(item, context);
            default:
                return `No description for ${item.type} type`;
        }
    }
    static getProjectText(item, people) {
        switch (item.sub) {
            case FeedItemSubType.CREATE:
                return `created the project`;
            case FeedItemSubType.INVITE:
                return `invited ${this.getPersonName(item.sub_id || item.related_id, people)} to the project`;
            case FeedItemSubType.REQUEST:
                return `requested access to ${item.text}`;
            case FeedItemSubType.APPROVE:
                return `approved ${this.getPersonName(item.sub_id || item.related_id, people, "'s")} access to the project`;
            case FeedItemSubType.DRAWING:
                return `uploaded a drawing`;
            case FeedItemSubType.SPECIFICATION:
                return `uploaded a specification`;
            case FeedItemSubType.PHOTO:
                return item.sub_id ? `added a photo` : `added ${item.text?.split(',').length} photos`;
            default:
                return `${item.sub} the project`;
        }
    }
    static getScheduleItemDesc(item, people, context) {
        switch (item.sub) {
            case FeedItemSubType.FILE:
                return `uploaded a schedule file`;
            case FeedItemSubType.VERSION: // Is this necessary since we have FILE?
                return `uploaded a schedule version`;
            case FeedItemSubType.CREATE:
                return `created ${context ? 'a schedule task' : ''}`;
            case FeedItemSubType.SUBJECT:
                return `updated the subject ${context ? 'of a schedule task' : ''}`;
            case FeedItemSubType.DESCRIPTION:
                return `updated the description ${context ? 'of a schedule task' : ''}`;
            case FeedItemSubType.PRIORITY:
                return `updated the priority ${context ? 'of a schedule task' : ''}`;
            case FeedItemSubType.PROGRESS:
                return `updated the progress ${context ? 'of a schedule task' : ''}`;
            case FeedItemSubType.STATUS:
                return `updated the status ${context ? 'of a schedule task' : ''}`;
            case FeedItemSubType.REMOVED:
                return `removed a schedule task`;
            case FeedItemSubType.PHOTO:
                return item.sub_id
                    ? `added a photo ${context ? 'to a schedule task' : ''}`
                    : `added ${item.text?.split(',').length} photos ${context ? 'to a schedule task' : ''}`;
            case FeedItemSubType.RFI:
                return `referenced an RFI ${context ? 'on a schedule task' : ''}`;
            case FeedItemSubType.ESTIMATE:
                return item.text || `Updated estimates ${context ? 'on a schedule task' : ''}`;
            case FeedItemSubType.LOG:
                return item.text || `added a log entry ${context ? 'to a schedule task' : ''}`;
            default:
                return `unexpected schedule sub: ${item.sub}`;
        }
    }
    static getRfiItemDesc(item, people, context = true) {
        switch (item.sub) {
            case FeedItemSubType.CREATE:
                return `created ${context ? 'an RFI' : 'the RFI'}`;
            case FeedItemSubType.INVITE:
                const person = people.find((p) => p._id === item.sub_id);
                return `added ${person ? person.first_name : 'someone'} to the distribution list ${context ? 'of an RFI' : ''}`;
            case FeedItemSubType.COMMENT:
                return `commented ${context ? 'on an RFI' : ''}`;
            case FeedItemSubType.DESCRIPTION:
                return `updated the description ${context ? 'of an RFI' : ''}`;
            case FeedItemSubType.SUBJECT:
                return `updated the subject ${context ? 'of an RFI' : ''}`;
            case FeedItemSubType.STATUS:
                return `updated the status ${context ? 'of an RFI' : ''}`;
            case FeedItemSubType.ACCEPT:
                return `accepted an answer ${context ? 'on an RFI' : ''}`;
            case FeedItemSubType.FILE:
                return `uploaded a file ${context ? 'to an RFI' : ''}`;
            case FeedItemSubType.PHOTO:
                return `added a photo ${context ? 'to an RFI' : ''}`;
            default:
                return `unexpected rfi sub: ${item.sub}`;
        }
    }
    static contractItemDescription(item, dbPrefix = 'contracts', context = true) {
        const contractOrSubcontract = dbPrefix === 'contracts' ? 'contract' : 'subcontract';
        switch (item.sub) {
            case FeedItemSubType.FILE:
                return `finished ${context ? 'the file' : ''}`;
            case FeedItemSubType.CONTRACT_UPDATED:
                return `updated ${context ? `the ${contractOrSubcontract} ` : ''}`;
            case FeedItemSubType.CREATE:
                return `created a ${context ? `${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.APPROVE:
                return `approved a suggestion ${context ? `in the ${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.REJECT:
                return `rejected a suggestion ${context ? `in the ${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.CONTRACT_SIGNED:
                return `signed ${context ? `the ${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.REQUEST:
                return `requested ${item?.sub_id ?? ''} a ${context ? `${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.COMMENT:
                return `created review mark in a ${context ? `${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.EDIT_COMMENT:
                return `updated review mark in a ${context ? `${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.PROPOSE:
                return `is proposing a new version of a ${context ? `${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.MARKED:
                return `marked as ${item.sub_id ?? ''} ${context ? `in the ${contractOrSubcontract}` : ''}`;
            case FeedItemSubType.REMOVED:
                return `removed suggestion ${context ? `from the ${contractOrSubcontract}` : ''}`;
            default:
                return ``;
        }
    }
    static submittalItemDesc(item, context = true) {
        switch (item.sub) {
            case FeedItemSubType.CREATE:
                if (item.sub_id)
                    return `created a new revision ${context ? 'on a sumbittal' : `${item?.text}`}`;
                else
                    return `created ${context ? 'a sumbittal' : ''}`;
            case FeedItemSubType.COMMENT:
                return `commented ${context ? 'on a submittal' : ''}`;
            case FeedItemSubType.DESCRIPTION:
                return `updated the description ${context ? 'of a submittal' : ''}`;
            case FeedItemSubType.SUBJECT:
                return `updated the subject ${context ? 'of a submittal' : ''}`;
            case FeedItemSubType.STATUS:
                return `updated the status ${context ? 'of a submittal' : ''}`;
            case FeedItemSubType.FILE:
                return `added a file ${context ? 'to a submittal' : ''}`;
            case FeedItemSubType.PHOTO:
                return item.data?.fileIds?.length > 1
                    ? `added ${item.data.fileIds.length} photos ${context ? 'to a submittal' : ''}`
                    : `added a photo ${context ? 'to a submittal' : ''}`;
            case FeedItemSubType.ACCEPT:
                return `accepted an answer ${context ? 'on a submittal' : ''}`;
            case FeedItemSubType.REJECT:
                return `rejected an answer ${context ? 'on a submittal' : ''}`;
            case FeedItemSubType.MARKED:
                return `added markups to file ${context ? 'on a submittal' : ''}`;
            default:
                return `no desc for sub: ${item.sub}`;
        }
    }
    static getPersonName(id, people, suffix = '') {
        if (!id)
            return `unknown`;
        const person = people.find((p) => p._id === id);
        return person ? `${person.first_name} ${person.last_name}${suffix}` : `unknown ${id}`;
    }
}
